import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

type ArmGen = {
  id: string;
  arch: string;
  processor: string;
  instance: string;
  gpu: boolean;
  status: "active" | "upcoming" | "future";
  color: string;
  year: string;
  note: string;
};

const GENERATIONS: ArmGen[] = [
  {
    id: "g2",
    arch: "ARMv8.2-A",
    processor: "Graviton2",
    instance: "G5g",
    gpu: true,
    status: "active",
    color: "#4ade80",
    year: "Now",
    note: "Production today · NVIDIA T4G GPU",
  },
  {
    id: "g4",
    arch: "ARMv9.0-A",
    processor: "Graviton4",
    instance: "C8g / M8g",
    gpu: false,
    status: "upcoming",
    color: "#22d3ee",
    year: "Next",
    note: "Pending GPU instance availability",
  },
  {
    id: "g5",
    arch: "ARMv9.2-A",
    processor: "Physical Silicon",
    instance: "—",
    gpu: false,
    status: "future",
    color: "#818cf8",
    year: "Future",
    note: "Cortex-X4 class · no cloud provider today",
  },
];

export function ArmArchitectureSlide() {
  const svgW = 900;
  const svgH = 400;
  const timelineY = 160;
  const nodeR = 22;
  const nodeSpacing = 300;
  const startX = 150;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <SlideTitle
          title="Arm Architecture Path"
          subtitle="Cloud-native Arm support — current capabilities and roadmap"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Arm architecture roadmap from ARMv8.2 through ARMv9.2"
          >
            {/* Timeline baseline */}
            <motion.line
              x1={startX - 40}
              y1={timelineY}
              x2={startX + nodeSpacing * 2 + 40}
              y2={timelineY}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={2}
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            />

            {/* Progress fill (only to "active" node) */}
            <motion.line
              x1={startX - 40}
              y1={timelineY}
              x2={startX + 30}
              y2={timelineY}
              stroke="var(--customer-accent)"
              strokeWidth={3}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            />

            {GENERATIONS.map((gen, i) => {
              const cx = startX + i * nodeSpacing;
              const isActive = gen.status === "active";
              const isUpcoming = gen.status === "upcoming";

              return (
                <motion.g
                  key={gen.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + i * 0.2, duration: 0.4 }}
                >
                  {/* Glow for active node */}
                  {isActive && (
                    <circle cx={cx} cy={timelineY} r={nodeR + 14} fill={gen.color} opacity={0.06} />
                  )}

                  {/* Outer ring */}
                  <circle
                    cx={cx}
                    cy={timelineY}
                    r={nodeR}
                    fill="rgba(10,10,14,0.8)"
                    stroke={gen.color}
                    strokeWidth={isActive ? 3 : 2}
                    strokeOpacity={isActive ? 1 : 0.5}
                    strokeDasharray={gen.status === "future" ? "4 3" : "none"}
                  />

                  {/* Inner fill */}
                  <circle
                    cx={cx}
                    cy={timelineY}
                    r={12}
                    fill={gen.color}
                    opacity={isActive ? 0.4 : 0.15}
                  />

                  {/* Year label inside node */}
                  <text
                    x={cx}
                    y={timelineY + 5}
                    textAnchor="middle"
                    fontSize={10}
                    fill={gen.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {gen.year}
                  </text>

                  {/* Architecture label — above */}
                  <text
                    x={cx}
                    y={timelineY - 48}
                    textAnchor="middle"
                    fontSize={18}
                    fill={gen.color}
                    fontWeight={800}
                    fontFamily="inherit"
                  >
                    {gen.arch}
                  </text>

                  {/* Processor label */}
                  <text
                    x={cx}
                    y={timelineY - 32}
                    textAnchor="middle"
                    fontSize={11}
                    fill="rgba(255,255,255,0.5)"
                    fontFamily="inherit"
                  >
                    {gen.processor}
                  </text>

                  {/* Card below */}
                  <rect
                    x={cx - 110}
                    y={timelineY + 40}
                    width={220}
                    height={90}
                    rx={10}
                    fill="rgba(255,255,255,0.025)"
                    stroke={gen.color}
                    strokeWidth={1}
                    strokeOpacity={0.15}
                  />

                  {/* Instance type */}
                  <text
                    x={cx}
                    y={timelineY + 64}
                    textAnchor="middle"
                    fontSize={12}
                    fill="white"
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    AWS: {gen.instance}
                  </text>

                  {/* GPU badge */}
                  <rect
                    x={cx - 32}
                    y={timelineY + 74}
                    width={64}
                    height={18}
                    rx={9}
                    fill={gen.gpu ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)"}
                  />
                  <text
                    x={cx}
                    y={timelineY + 87}
                    textAnchor="middle"
                    fontSize={9}
                    fill={gen.gpu ? "#4ade80" : "rgba(255,255,255,0.3)"}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {gen.gpu ? "GPU ✓" : "No GPU"}
                  </text>

                  {/* Note */}
                  <text
                    x={cx}
                    y={timelineY + 110}
                    textAnchor="middle"
                    fontSize={9}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="inherit"
                    fontStyle="italic"
                  >
                    {gen.note}
                  </text>

                  {/* Connector dashes between nodes */}
                  {i < GENERATIONS.length - 1 && (
                    <motion.line
                      x1={cx + nodeR + 8}
                      y1={timelineY}
                      x2={cx + nodeSpacing - nodeR - 8}
                      y2={timelineY}
                      stroke={isUpcoming || isActive ? gen.color : "rgba(255,255,255,0.1)"}
                      strokeWidth={1.5}
                      strokeDasharray="6 4"
                      strokeOpacity={0.4}
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.9 + i * 0.2, duration: 0.4 }}
                    />
                  )}
                </motion.g>
              );
            })}

            {/* Bottom annotation */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6 }}
            >
              <rect
                x={120}
                y={svgH - 50}
                width={svgW - 240}
                height={36}
                rx={8}
                fill="rgba(74,222,128,0.04)"
                stroke="rgba(74,222,128,0.1)"
                strokeWidth={1}
              />
              <text
                x={svgW / 2}
                y={svgH - 27}
                textAnchor="middle"
                fontSize={11}
                fill="var(--customer-accent-light)"
                fontWeight={500}
                fontFamily="inherit"
              >
                For exact ARMv9.2-A parity, we&apos;ll collaborate on a hardware integration path
              </text>
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
